import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AppConfigService } from '@/services/app-config.service';
import { AsyncPipe, CurrencyPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import {
  PhotosService,
  PropertiesService,
  PropertyAttribute,
  PropertyType,
  RealProperty,
  SellingStatus
} from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CertificationBadgeComponent } from '../certification-badge/certification-badge.component';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-property-profil',
  templateUrl: './property-profil.component.html',
  styleUrls: ['./property-profil.component.scss'],
  standalone: true,
  imports: [
    MatCardModule,
    CertificationBadgeComponent,
    NgIf,
    MatIconModule,
    RouterLink,
    AsyncPipe,
    CurrencyPipe,
    TranslateModule,
    TooltipDirective
  ]
})
export class PropertyProfilComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() mainPhotoTooltip: string;
  @Input() mainPhotoIsClickable = true;
  @Output() mainPhotoClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  subs: Subscription[] = [];
  mainPhoto: SafeUrl;
  isPropertyOwner = false;
  isCondo = false;
  isInCondo = false;
  isCondoPropParent = false;
  propertySubTitle: string;
  dropBoxAddress: string;
  badgeImageName: string;
  parentBadgeImageName: string;
  condoBadgeImageName: string;
  summaryAttrs: { [key: string]: PropertyAttribute } = {};
  propertyDescSubject: Subject<string> = new BehaviorSubject<string>('');
  propertyDesc: Observable<string> = this.propertyDescSubject.asObservable();
  isOnSelling: boolean;
  price = 0;

  constructor(
    private appConfig: AppConfigService,
    private propHelper: PropertyHelper,
    private dialog: MatDialog,
    public userHelper: UserHelper,
    private propertyService: PropertiesService,
    private photoService: PhotosService,
    private domSanitizer: DomSanitizer,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit(): void {
    this.isPropertyOwner = this.userHelper.isPropertyOwner(this.prop);
    this.isCondo = this.propHelper.isCondo(this.prop);
    this.isInCondo = this.propHelper.isInCondo(this.prop);
    this.isCondoPropParent = this.prop?.parent ? this.propHelper.isCondo(this.prop.parent) : false;
    this.condoBadgeImageName = this.getCondoBadgeImageName();
    if (this.propHelper.isCondo(this.prop)) {
      this.propertySubTitle = 'Syndic ' + this.propHelper.getOwnersNameFull(this.prop);
    } else if (this.prop.onGoingSelling && !this.prop.demo) {
      if (this.prop.sharedBy && this.prop.sharedBy.uid != this.prop.owner.uid) {
        this.propertySubTitle = 'Proposé par ' + this.propHelper.getSharedUserNameFull(this.prop);
      } else {
        this.propertySubTitle = 'Vendu par ' + this.propHelper.getOwnersNameFull(this.prop);
      }
    } else {
      this.propertySubTitle = 'Propriété de ' + this.propHelper.getOwnersNameFull(this.prop);
    }
    this.mainPhoto = this.propHelper.getEmptyMainPhotoImage();
    this.badgeImageName = this.propHelper.getBadgeImageName(this.prop.type);
    this.dropBoxAddress = this.propHelper.getDropBoxAddress(this.prop);
    this.parentBadgeImageName = this.propHelper.getBadgeImageName(this.prop?.parent?.type);
    this.isOnSelling = !this.prop.demo && this.prop.onGoingSelling?.status === SellingStatus.SELLING;
    this.price = this.prop.onGoingSelling?.price ? this.prop.onGoingSelling.price : 0;
    this.subs.push(
      this.appConfig.mainPhotoUpdated.subscribe({
        next: () => {
          this.loadPropertyMainPhoto(this.prop, true);
        }
      })
    );
    this.loadPropertyMainPhoto(this.prop);
    this.loadPropertyDesc();
    this.subs.push(
      this.appConfigService.objectUpdated.subscribe((o) => {
        if (ObjectUpdatedType.ATTRIBUTE === o) {
          this.loadPropertyDesc();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadPropertyDesc() {
    this.subs.push(
      this.propertyService.getRealPropertyAttributesSummary(this.prop.uid).subscribe((data) => {
        this.summaryAttrs = {};
        data.forEach((attr) => (this.summaryAttrs[attr.code] = attr));
        let desc = this.propHelper.getTypeName(this.prop.type);
        if (this.summaryAttrs[environment.SURFACE_LIVING_CODE] || this.summaryAttrs[environment.SURFACE_CODE]) {
          const surface: number = +(
            this.summaryAttrs[environment.SURFACE_LIVING_CODE]
              ? this.summaryAttrs[environment.SURFACE_LIVING_CODE]
              : this.summaryAttrs[environment.SURFACE_CODE]
          ).value;
          desc += ' - ' + surface.toFixed() + ' m²';
        }
        if (
          this.summaryAttrs[environment.ROOM_NB_CODE] &&
          (this.prop.type === PropertyType.HOUSE || this.prop.type === PropertyType.FLAT)
        ) {
          desc += ' - ' + this.summaryAttrs[environment.ROOM_NB_CODE].value + ' pièces';
        }
        this.propertyDescSubject.next(desc);
      })
    );
  }

  showDropBoxInfo() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        maxWidth: '30rem',
        data: {
          title: 'Transfert rapide de documents',
          content:
            '<p>Cette adresse email est la boîte de dépôt de votre bien.</p>' +
            '<p>Elle permet un transfert simplfié des documents, via un email contenant les pièces jointes souhaitées.</p>' +
            "<p>Référez-vous à la rubrique d'aide correspondante pour plus d'informations...</p>"
        }
      });
    }
  }

  getCondoBadgeImageName() {
    return this.propHelper.getBadgeImageName(PropertyType.CONDOMINIUM);
  }

  private loadPropertyMainPhoto(prop: RealProperty, forceTs?: boolean) {
    const photoTs = forceTs ? new Date().getTime().toString() : this.prop.mainPhotoTs;
    this.subs.push(
      this.photoService.getMainPhotoImage(this.prop.uid, '351x200', photoTs).subscribe((photo) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.mainPhoto = this.domSanitizer.bypassSecurityTrustUrl(reader.result.toString());
        };
        reader.readAsDataURL(photo);
      })
    );
  }

  onMainPhotoClick() {
    this.mainPhotoClicked.emit(true);
  }
}
